/* AdminWaitlist · /admin/waitlist — review + approve waitlist signups
 *
 * Lists everyone who joined /waitlist with filter / search / sort.
 * Admin can:
 *   • View pending signups
 *   • Bulk-approve (sends magic-link invite emails)
 *   • Export to CSV for outreach campaigns
 *   • Mark statuses (pending / contacted / approved / rejected)
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Loader2, Download, Search, CheckCircle2, Clock, XCircle, Mail,
  Sparkles, Building2, AlertTriangle,
} from "lucide-react";

const ROLE_LABEL    = { seller: "Seller", buyer: "Buyer", both: "Both" };
const REVENUE_LABEL = {
  under_500k: "<$500K", "500k_1m": "$500K-$1M", "1m_5m": "$1M-$5M",
  "5m_25m":   "$5M-$25M", "25m_plus": "$25M+",
};
const STATUS_LABEL  = {
  pending:   { label: "Pending",    color: "bg-amber-50 text-amber-800 border-amber-200" },
  contacted: { label: "Contacted",  color: "bg-sky-50 text-sky-800 border-sky-200" },
  approved:  { label: "Approved",   color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  signed_up: { label: "Signed up",  color: "bg-violet-50 text-violet-800 border-violet-200" },
  rejected:  { label: "Rejected",   color: "bg-rose-50 text-rose-800 border-rose-200" },
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "-";

export default function AdminWaitlist() {
  const [items, setItems]   = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", role: "", q: "" });
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.role)   params.role   = filter.role;
      if (filter.q)      params.q      = filter.q;
      const { data } = await api.get("/admin/waitlist", { params });
      setItems(data.items || []);
      setByStatus(data.by_status || {});
      setTotal(data.total || 0);
      setSelected(new Set());  // reset selection on reload
    } catch (e) {
      toast.error("Couldn't load waitlist");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.role]);

  const approve = async (item) => {
    if (!confirm(`Approve ${item.email}? They'll get a magic-link signup email.`)) return;
    setBusyId(item.id);
    try {
      await api.post(`/admin/waitlist/${item.id}/approve`);
      toast.success(`Approved · invite email sent to ${item.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };
  const updateStatus = async (item, status) => {
    setBusyId(item.id);
    try {
      await api.patch(`/admin/waitlist/${item.id}`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  // ── Bulk-approve (Phase 4) ──────────────────────────────────────────
  const approvable = items.filter(it => it.status === "pending" || it.status === "contacted");
  const selectableIds = approvable.map(it => it.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const bulkApprove = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Approve ${ids.length} waitlist signups? Each will receive a magic-link invite email.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/bulk-approve", { ids });
      toast.success(
        `${data.approved} approved · ${data.emails_sent} invite emails sent` +
        (data.already_approved ? ` · ${data.already_approved} already approved` : "")
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Bulk-approve failed");
    } finally { setBulkBusy(false); }
  };

  const downloadCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/waitlist/export`;
    // Add token from localStorage as auth header isn't possible on direct download
    const a = document.createElement("a");
    a.href = url;
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="admin-waitlist-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display text-2xl tracking-tight">Waitlist</h1>
          <p className="text-[12.5px] text-foreground/55 mt-1">
            {total.toLocaleString()} signups · review &amp; send approval invites.
            {byStatus.signed_up ? (
              <> · <span className="text-violet-700 font-medium">{byStatus.signed_up} converted</span></>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button size="sm" onClick={bulkApprove} disabled={bulkBusy}
              className="rounded-full bg-foreground text-background"
              data-testid="bulk-approve">
              {bulkBusy
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Sending {selected.size} invites…</>
                : <><Sparkles className="w-4 h-4 mr-1.5"/>Approve + invite {selected.size} selected</>}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={downloadCsv} className="rounded-full" data-testid="export-csv">
            <Download className="w-4 h-4 mr-1.5"/>Export CSV
          </Button>
          <Button size="sm" onClick={load} className="rounded-full" data-testid="refresh">
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { k: "pending",   label: "Pending",   icon: Clock },
          { k: "contacted", label: "Contacted", icon: Mail },
          { k: "approved",  label: "Approved",  icon: CheckCircle2 },
          { k: "rejected",  label: "Rejected",  icon: XCircle },
        ].map(s => (
          <div key={s.k} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55">{s.label}</div>
              <s.icon className="w-4 h-4 text-foreground/45"/>
            </div>
            <div className="text-2xl font-semibold tabular-nums mt-1">{byStatus[s.k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
          <Input placeholder="Search email / name / company"
            value={filter.q}
            onChange={e => setFilter({...filter, q: e.target.value})}
            onKeyDown={e => e.key === "Enter" && load()}
            className="pl-9 bg-background"
            data-testid="filter-search"/>
        </div>
        <select value={filter.status}
          onChange={e => setFilter({...filter, status: e.target.value})}
          className="h-9 px-3 bg-background border border-border rounded-md text-[12.5px]"
          data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="approved">Approved</option>
          <option value="signed_up">Signed up</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={filter.role}
          onChange={e => setFilter({...filter, role: e.target.value})}
          className="h-9 px-3 bg-background border border-border rounded-md text-[12.5px]"
          data-testid="filter-role">
          <option value="">All roles</option>
          <option value="seller">Sellers</option>
          <option value="buyer">Buyers</option>
          <option value="both">Both</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-foreground/55"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-foreground/55">No signups match those filters.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-secondary/40">
              <tr className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
                <th className="p-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Select all approvable rows"
                    className="w-3.5 h-3.5 accent-foreground" data-testid="select-all"/>
                </th>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Email · Name</th>
                <th className="text-left p-3">Company</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Revenue</th>
                <th className="text-left p-3">Joined</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Signed up</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const isApprovable = it.status === "pending" || it.status === "contacted";
                return (
                <tr key={it.id} className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`waitlist-row-${it.id}`}>
                  <td className="p-3">
                    {isApprovable && (
                      <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleOne(it.id)}
                        aria-label={`Select ${it.email}`}
                        className="w-3.5 h-3.5 accent-foreground" data-testid={`select-${it.id}`}/>
                    )}
                  </td>
                  <td className="p-3 text-foreground/60 tabular-nums">{it.position}</td>
                  <td className="p-3">
                    <div className="font-medium text-foreground">{it.email}</div>
                    <div className="text-[11.5px] text-foreground/55">{it.name}</div>
                  </td>
                  <td className="p-3 text-foreground/80">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3 h-3 text-foreground/40"/>
                      <span>{it.company}</span>
                    </div>
                  </td>
                  <td className="p-3"><span className="text-[11px] mono uppercase tracking-wider text-foreground/70">{ROLE_LABEL[it.role] || it.role}</span></td>
                  <td className="p-3 text-foreground/70">{REVENUE_LABEL[it.revenue_band] || "-"}</td>
                  <td className="p-3 text-foreground/55 text-[11.5px]">{fmtDate(it.created_at)}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${(STATUS_LABEL[it.status] || STATUS_LABEL.pending).color}`}>
                      {(STATUS_LABEL[it.status] || STATUS_LABEL.pending).label}
                    </span>
                  </td>
                  <td className="p-3 text-[11.5px]">
                    {it.signed_up_at
                      ? <span className="text-violet-700 font-medium" data-testid={`signed-up-${it.id}`}>{fmtDate(it.signed_up_at)}</span>
                      : <span className="text-foreground/30">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    {isApprovable ? (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline"
                          onClick={() => updateStatus(it, "contacted")}
                          disabled={busyId === it.id}
                          className="h-7 px-2 text-[11px] rounded-full"
                          title="Mark contacted">
                          <Mail className="w-3 h-3"/>
                        </Button>
                        <Button size="sm"
                          onClick={() => approve(it)}
                          disabled={busyId === it.id}
                          className="h-7 px-3 text-[11px] rounded-full bg-foreground text-background"
                          data-testid={`approve-${it.id}`}>
                          {busyId === it.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <><Sparkles className="w-3 h-3 mr-1"/>Approve + invite</>}
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => updateStatus(it, "rejected")}
                          disabled={busyId === it.id}
                          className="h-7 px-2 text-[11px] rounded-full text-rose-700 hover:bg-rose-50">
                          <XCircle className="w-3 h-3"/>
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-foreground/40 italic">
                        {it.status === "signed_up" ? "✓ Converted"
                          : it.status === "approved" ? "✓ Invite sent" : ""}
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-4 text-[12.5px] text-amber-900 flex gap-2 items-start">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
        <div>
          <strong>Approving</strong> sends a magic-link signup email immediately. Once they click and create their account, the row will show "Invite sent." Set <strong>PUBLIC_SIGNUP_ENABLED=true</strong> in env vars to open public signups for everyone (then this page becomes optional).
        </div>
      </div>
    </div>
  );
}
